/**
 * LANDING PAGE COMPONENT - DO NOT USE IN APP
 *
 * Component: Hero
 * Purpose: Hero section with scroll-reveal workspace image animation
 * Location: /src/landing/components/Hero.tsx
 *
 * Features:
 * - Large serif heading "Concierge." with Kiln branding
 * - Search input with glass morphism effect
 * - Workspace image that reveals and scales on scroll
 * - Background blur effects with pulse animations
 * - Sequential fade-in animations for content
 */

import React, { useRef, FormEvent } from "react";
import { ArrowUp } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import kilnLogo from "../assets/kiln-logo.webp";
import workspaceHero from "../assets/workspace-hero.webp";

const Hero = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  // Transform values for the reveal animation
  const imageScale = useTransform(scrollYProgress, [0, 0.5], [0.8, 1]);
  const imageOpacity = useTransform(scrollYProgress, [0, 0.3], [0, 1]);
  const borderRadius = useTransform(scrollYProgress, [0, 0.5], [80, 0]);
  const contentOpacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.2], [0, -100]);

  const goToLogin = () => {
    navigate('/login');
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    navigate('/login');
  };

  return (
    <div ref={containerRef} className="relative h-[200vh]">
      {/* Sticky container for the hero content */}
      <div className="sticky top-0 h-screen overflow-hidden">
        {/* Background image that reveals on scroll */}
        <motion.div
          className="absolute inset-0 z-0"
          style={{
            opacity: imageOpacity,
            scale: imageScale,
            borderRadius: borderRadius,
          }}
        >
          <img
            src={workspaceHero}
            alt="Modern workspace"
            className="w-full h-full object-cover"
          />
          {/* Subtle overlay for text readability */}
          <div className="absolute inset-0 bg-gradient-to-b from-background/30 via-transparent to-background/50" />
        </motion.div>

        {/* Original hero content */}
        <motion.section
          className="relative min-h-screen flex flex-col items-center justify-center pt-20 sm:pt-24 px-4 sm:px-6 md:px-8 overflow-hidden z-10"
          style={{ opacity: contentOpacity, y: contentY }}
        >
          {/* Background blur effects - decorative animated elements */}
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl landing-animate-pulse-glow" />
            <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-accent/10 rounded-full blur-3xl landing-animate-pulse-glow" style={{ animationDelay: '1.5s' }} />
          </div>

          {/* Main hero content */}
          <div className="relative z-10 text-center max-w-4xl mx-auto w-[80%] md:w-[70%] lg:w-[60%] xl:w-[40%]">
            {/* Main heading */}
            <h1 className="font-serif font-normal text-foreground mb-3 sm:mb-4 animate-fade-in text-5xl sm:text-6xl md:text-7xl lg:text-8xl xl:text-[7.75rem] leading-tight">
              Concierge.
            </h1>

            {/* Kiln branding */}
            <div className="flex items-baseline justify-center gap-2 sm:gap-3 mb-6 sm:mb-8 animate-fade-in" style={{ animationDelay: '0.1s' }}>
              <span className="text-muted-foreground text-xl sm:text-2xl md:text-3xl lg:text-4xl">by</span>
              <img src={kilnLogo} alt="Kiln" className="h-5 sm:h-6 md:h-7 lg:h-9 translate-y-0.5" />
            </div>

            {/* Tagline */}
            {/* <p className="text-lg md:text-xl landing-gradient-text font-medium mb-12 animate-fade-in" style={{ animationDelay: '0.2s' }}>
              Your digital community team member, always here to help.
              <br />
              Personalized support for every member, 24/7.
            </p> */}

            {/* Search input with glass effect */}
            <motion.form
              layout
              onSubmit={handleSubmit}
              className="relative max-w-4xl mx-auto animate-fade-in w-full px-4 sm:px-0"
              style={{ animationDelay: '0.4s' }}
            >
              <div
                role="button"
                tabIndex={0}
                onClick={goToLogin}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    goToLogin();
                  }
                }}
                className="landing-glass rounded-full px-4 sm:px-6 py-3 sm:py-4 flex items-center gap-2 sm:gap-4 landing-glow-primary cursor-pointer"
              >
                <input
                  type="text"
                  placeholder="How can I assist you today?"
                  readOnly
                  onFocus={(e) => {
                    e.target.blur();
                    goToLogin();
                  }}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    goToLogin();
                  }}
                  className="flex-1 bg-transparent text-foreground placeholder:text-muted-foreground outline-none text-sm sm:text-base md:text-lg cursor-pointer"
                />
                <motion.button
                  type="button"
                  onClick={goToLogin}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="bg-primary rounded-full p-2 transition-all"
                >
                  <ArrowUp className="w-5 h-5 text-primary-foreground" />
                </motion.button>
              </div>
            </motion.form>
          </div>
        </motion.section>
      </div>
    </div>
  );
};

export default Hero;
